<template>
  <div class="sa-voice-context">
    <!-- One row, always present. A chip that disappeared when nothing was in
         context would leave the user with no explanation for why "move this"
         just got refused, and nowhere to press Scan to fix it. -->
    <div class="sa-voice-context-pill">
      <svg
        class="sa-voice-context-eye"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </svg>

      <span class="sa-voice-context-copy">{{ chip }}</span>

      <!-- Disabled rather than hidden in Stage 1. The button is how the user
           learns the capability exists, and the title says what it needs; a
           control that appears only once the phone app ships is a control
           nobody goes looking for. -->
      <button
        type="button"
        class="sa-voice-context-scan"
        :disabled="!scanAvailable"
        :title="scanTitle"
        @click="$emit('scan')"
      >
        Scan
      </button>
    </div>

    <p class="sa-voice-context-hint">{{ hint }}</p>
  </div>
</template>

<script>
import { chipCopy, hintCopy } from "../../utils/voiceContextSource.js";

export default {
  name: "VoiceContextChip",
  props: {
    // The store's `voiceScreenContext`. Read only; the chip never writes it —
    // PuckSection publishes and the dock merges.
    context: {
      type: Object,
      default: () => ({}),
    },
    // False until the phone app can hand back a scan (Stage 2, P2.3).
    scanAvailable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["scan"],
  computed: {
    chip() {
      return chipCopy(this.context);
    },
    hint() {
      return hintCopy(this.context);
    },
    scanTitle() {
      if (!this.scanAvailable) return "Scan needs the phone app (Stage 2)";
      return "Scan a puck's QR code";
    },
  },
};
</script>

<style scoped>
/* Plain CSS. public/assets/styles.css is pre-compiled and committed, so a
   Tailwind class not already in it does nothing at runtime. */

.sa-voice-context {
  font-family: Montserrat, sans-serif;
}

.sa-voice-context-pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  /* Padding on the left only: the Scan button carries its own, and 44 px of
     button against a rounded edge looks wrong with more. */
  padding: 0.25rem 0.25rem 0.25rem 0.75rem;

  border: 1px solid #bae6fd;
  border-radius: 9999px;
  background: #f0f9ff;
  color: #0c4a6e;
}

.sa-voice-context-eye {
  flex: none;
  color: #0284c7;
}

.sa-voice-context-copy {
  /* Takes the slack so the button stays pinned right, and truncates rather
     than wrapping: a two-line pill would shove the transcript down the dock. */
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  font-size: 15px;
}

.sa-voice-context-scan {
  flex: none;

  /* 44 px is the floor for a gloved tap; the dock is used at a bench. */
  min-height: 44px;
  padding: 0 1rem;

  border: 0;
  border-radius: 9999px;
  background: #0c4a6e;
  color: #ffffff;
  font-family: inherit;
  font-size: 15px;
  cursor: pointer;
}

.sa-voice-context-scan:disabled {
  background: #cbd5e1;
  color: #475569;
  /* Not `not-allowed`: the title explains what is missing, and the cursor
     saying "no" reads as a bug rather than as "not yet". */
  cursor: default;
}

.sa-voice-context-scan:focus-visible {
  outline: 3px solid #0284c7;
  outline-offset: 2px;
}

.sa-voice-context-hint {
  margin: 0.375rem 0 0;
  font-size: 12px;
  line-height: 1.35;
  color: #475569;
}
</style>
